import { useEffect, useState } from "react";
import { useI18n, LANGS } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import client, { apiErr, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Globe, MessageSquare, Loader2, Smartphone, Copy, RefreshCw, Tag, Plus, Trash2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Settings() {
  const { t, locale, setLocale } = useI18n();
  const { user, reload } = useAuth();
  const canManage = user?.role === "owner" || user?.role === "admin";
  const [msgOn, setMsgOn] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    client.get("/settings").then((r) => setMsgOn(r.data.messagingEnabled !== false)).catch(() => {});
  }, []);

  const toggleMessaging = async (next) => {
    setSaving(true);
    setMsgOn(next);
    try {
      await client.patch("/settings", { messagingEnabled: next });
      toast.success(next ? t("settings.msgOn", "Messaging enabled") : t("settings.msgOff", "Messaging disabled"));
      await reload();
    } catch (e) {
      setMsgOn(!next);
      toast.error(apiErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const [widgetToken, setWidgetToken] = useState(null);
  const [widgetBusy, setWidgetBusy] = useState(false);
  const summaryUrl = widgetToken ? `${API}/widget/summary?token=${widgetToken}` : "";

  useEffect(() => {
    client.get("/widget/token").then((r) => setWidgetToken(r.data.token || null)).catch(() => {});
  }, []);

  const genToken = async () => {
    setWidgetBusy(true);
    try {
      const r = await client.post("/widget/token");
      setWidgetToken(r.data.token);
      toast.success(t("settings.widgetGenerated", "Widget link ready"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setWidgetBusy(false); }
  };

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(summaryUrl); toast.success(t("settings.copied", "Copied to clipboard")); }
    catch { toast.error(t("settings.copyFail", "Could not copy — select and copy manually.")); }
  };

  const [jobTypes, setJobTypes] = useState([]);
  const [newType, setNewType] = useState("");
  const [jtBusy, setJtBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const loadTypes = () => client.get("/job-types").then((r) => setJobTypes(r.data || [])).catch(() => {});
  useEffect(() => { loadTypes(); }, []);

  const addType = async () => {
    const label = newType.trim();
    if (!label) return;
    setJtBusy(true);
    try { await client.post("/job-types", { label }); setNewType(""); await loadTypes(); toast.success(t("settings.typeAdded", "Job type added")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setJtBusy(false); }
  };
  const saveType = async (id) => {
    const label = editLabel.trim();
    if (!label) return;
    try { await client.patch(`/job-types/${id}`, { label }); setEditId(null); await loadTypes(); toast.success(t("settings.typeSaved", "Saved")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const deleteType = async (id) => {
    try { await client.delete(`/job-types/${id}`); await loadTypes(); toast.success(t("settings.typeRemoved", "Removed")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h2 className="font-display text-2xl font-bold text-slate-900">{t("settings.title", "Settings")}</h2>
      </div>

      {canManage && (
        <Card className="p-6 border-slate-200 max-w-xl" data-testid="settings-messaging-card">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="h-4 w-4 text-brand" />
            <h3 className="font-semibold text-slate-900">{t("settings.messaging", "Messaging")}</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">{t("settings.messagingDesc", "Turn the in-app messaging system on or off across the portal and the mobile app. When off, message screens are hidden and all messaging requests are blocked.")}</p>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-slate-800">{t("settings.enableMessaging", "Enable messaging")}</p>
              <p className="text-xs text-slate-500">{msgOn ? t("settings.msgStatusOn", "Messaging is live for everyone.") : t("settings.msgStatusOff", "Messaging is disabled and hidden.")}</p>
            </div>
            <div className="flex items-center gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              <Switch data-testid="settings-messaging-toggle" checked={msgOn} disabled={saving} onCheckedChange={toggleMessaging} />
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 border-slate-200 max-w-xl" data-testid="settings-widget-card">
        <div className="flex items-center gap-2 mb-1">
          <Smartphone className="h-4 w-4 text-brand" />
          <h3 className="font-semibold text-slate-900">{t("settings.widget", "iPhone home-screen widget")}</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">{t("settings.widgetDesc", "Show a live 'who's clocked in' and today's hours tile on your iPhone home screen using the free Scriptable app. This link is a private, read-only key — it shows no pay figures. Keep it secret; anyone with it can view this summary.")}</p>

        {widgetToken ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500 mb-1">{t("settings.widgetUrl", "Widget data URL")}</p>
              <code className="block text-xs text-slate-700 break-all" data-testid="widget-url">{summaryUrl}</code>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="bg-brand hover:bg-brand-dark" data-testid="widget-copy" onClick={copyUrl}><Copy className="h-4 w-4 mr-1.5" />{t("settings.copyLink", "Copy link")}</Button>
              <Button size="sm" variant="outline" data-testid="widget-rotate" disabled={widgetBusy} onClick={genToken}>
                {widgetBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}{t("settings.regenerate", "Regenerate")}
              </Button>
            </div>
            <details className="text-sm text-slate-600">
              <summary className="cursor-pointer font-medium text-slate-700" data-testid="widget-howto-toggle">{t("settings.widgetHowto", "How to add it to your iPhone")}</summary>
              <ol className="list-decimal ml-5 mt-2 space-y-1 text-slate-500">
                <li>Install the free <b>Scriptable</b> app from the App Store.</li>
                <li>Open Scriptable → tap <b>+</b> → paste the script we provided → name it <b>iClean</b>.</li>
                <li>When prompted, paste the <b>Widget data URL</b> above (or it's already in the script).</li>
                <li>Long-press your home screen → <b>+</b> → choose <b>Scriptable</b> → pick the small/medium widget.</li>
                <li>Long-press the new widget → <b>Edit Widget</b> → select the <b>iClean</b> script.</li>
              </ol>
            </details>
          </div>
        ) : (
          <Button className="bg-brand hover:bg-brand-dark" data-testid="widget-generate" disabled={widgetBusy} onClick={genToken}>
            {widgetBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Smartphone className="h-4 w-4 mr-1.5" />}{t("settings.generateWidget", "Generate my widget link")}
          </Button>
        )}
      </Card>

      {canManage && (
        <Card className="p-6 border-slate-200 max-w-xl" data-testid="settings-jobtypes-card">
          <div className="flex items-center gap-2 mb-1">
            <Tag className="h-4 w-4 text-brand" />
            <h3 className="font-semibold text-slate-900">{t("settings.jobTypes", "Job types")}</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">{t("settings.jobTypesDesc", "The list of Field job types shown when creating a job. Add, rename or remove them here.")}</p>
          <div className="space-y-2 mb-4">
            {jobTypes.map((jt) => (
              <div key={jt.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2" data-testid={`jobtype-row-${jt.id}`}>
                {editId === jt.id ? (
                  <>
                    <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="h-8 flex-1" data-testid={`jobtype-edit-input-${jt.id}`}
                      onKeyDown={(e) => e.key === "Enter" && saveType(jt.id)} />
                    <Button size="sm" className="h-8 bg-brand hover:bg-brand-dark" data-testid={`jobtype-save-${jt.id}`} onClick={() => saveType(jt.id)}><Check className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditId(null)}>{t("common.cancel", "Cancel")}</Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm font-medium text-slate-800" data-testid={`jobtype-label-${jt.id}`}>{jt.label}</span>
                    <Button size="sm" variant="ghost" className="h-8 text-slate-500" data-testid={`jobtype-edit-${jt.id}`} onClick={() => { setEditId(jt.id); setEditLabel(jt.label); }}>{t("common.edit", "Edit")}</Button>
                    <Button size="sm" variant="ghost" className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`jobtype-delete-${jt.id}`} onClick={() => deleteType(jt.id)}><Trash2 className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newType} onChange={(e) => setNewType(e.target.value)} placeholder={t("settings.newJobType", "New job type name…")}
              data-testid="jobtype-new-input" className="flex-1" onKeyDown={(e) => e.key === "Enter" && addType()} />
            <Button className="bg-brand hover:bg-brand-dark" data-testid="jobtype-add-btn" disabled={jtBusy || !newType.trim()} onClick={addType}>
              {jtBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />{t("common.add", "Add")}</>}
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-6 border-slate-200 max-w-xl">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="h-4 w-4 text-brand" />
          <h3 className="font-semibold text-slate-900">{t("settings.language", "Language / Preferences")}</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">{t("settings.langDesc", "Choose the language for your portal.")}</p>
        <div className="flex flex-wrap gap-2" data-testid="settings-language-switcher">
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`settings-language-${l.code}`}
              onClick={() => setLocale(l.code)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                locale === l.code ? "bg-brand text-white border-brand" : "bg-white border-slate-200 text-slate-600 hover:border-brand/40"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
